import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import api, { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
import { shareVisitorState } from '../cognitive-flows/utils/shareAccess';
import Login from './login';
import SharedReportView from '../cognitive-flows/components/chat/SharedReportView';
import '../cognitive-flows/styles/global.css';

/**
 * Cookie-less visitor hitting /share/:token — stash the intended path so login
 * returns here, then show the normal login screen.
 */
export function ShareGate({ app_url }) {
    const { token } = useParams();
    useEffect(() => {
        sessionStorage.setItem('cfShareReturn', `/share/${token}`);
    }, [token]);
    return <Login app_url={app_url} />;
}

/** Same gate for canvas share links (/canvas-share/:token). */
export function CanvasShareGate({ app_url }) {
    const { token } = useParams();
    useEffect(() => {
        sessionStorage.setItem('cfShareReturn', `/canvas-share/${token}`);
    }, [token]);
    return <Login app_url={app_url} />;
}

/**
 * Mounted in the authed shell. After a login that was initiated from a share
 * link, bounce the user back to /share/:token (which then accepts + redirects
 * into the chat). Renders nothing.
 */
export function ShareReturn() {
    const navigate = useNavigate();
    useEffect(() => {
        const back = sessionStorage.getItem('cfShareReturn');
        if (back && (back.startsWith('/share/') || back.startsWith('/canvas-share/'))) {
            sessionStorage.removeItem('cfShareReturn');
            navigate(back, { replace: true });
        }
    }, [navigate]);
    return null;
}

const st = {
    page: { minHeight: '100vh', background: '#eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, system-ui, sans-serif' },
    card: { maxWidth: '400px', width: '100%', background: '#fff', border: '1px solid #d3ddea', borderRadius: '16px', padding: '30px 28px', boxShadow: '0 4px 18px rgba(var(--cux-ink-rgb, 19, 40, 72), 0.10)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px' },
    brand: { fontSize: '13px', fontWeight: 800, color: 'var(--primary, var(--cux-ink, #132848))', letterSpacing: '0.02em' },
    title: { fontSize: '18px', fontWeight: 800, color: 'var(--text-primary, #0f172a)', margin: 0 },
    text: { fontSize: '13px', color: '#475569', margin: 0, lineHeight: 1.6 },
    spinner: { width: '30px', height: '30px', margin: '4px auto', borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: 'var(--primary, var(--cux-ink, #132848))', animation: 'spin 0.8s linear infinite' },
    btn: { border: 'none', background: 'var(--primary, var(--cux-ink, #132848))', color: '#fff', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
};

export default function ShareAcceptPage({ app_url }) {
    const { token } = useParams();
    const navigate = useNavigate();
    const [phase, setPhase] = useState('accepting'); // accepting | view | not_allowed | gone | no_store | error
    const [report, setReport] = useState(null);

    useEffect(() => {
        const storesRaw = Cookies.get('stores');
        let stores = null;
        if (storesRaw) {
            try {
                stores = JSON.parse(storesRaw);
            } catch {
                // Malformed cookie: treat as no stores rather than throwing and
                // blanking the page.
                stores = null;
            }
        }
        const visitor = shareVisitorState({
            userEmail: Cookies.get('user_email'),
            shop: Cookies.get('shop'),
            stores,
        });
        // Signed in but store-less: stop here. Calling accept would 401 and the
        // interceptor would bounce to /login, which is the loop this fixes.
        if (visitor === 'store_less') { setPhase('no_store'); return; }

        sessionStorage.setItem('cfShareReturn', `/share/${token}`);
        if (app_url) setCognitiveFlowsApiBase(app_url);
        api.post(`/chat/share/${encodeURIComponent(token)}/accept`)
            .then(({ data }) => {
                sessionStorage.removeItem('cfShareReturn');
                if (data.mode === 'view') {
                    return api.get(`/chat/share/${encodeURIComponent(token)}/artifact`)
                        .then(({ data: art }) => { setReport(art); setPhase('view'); });
                }
                navigate(`/cognitive-flows/chat?openSession=${encodeURIComponent(data.sessionId)}`, { replace: true });
                return undefined;
            })
            .catch((err) => {
                const code = err.response?.data?.code;
                // A 401 means the axios interceptor is redirecting to /login — leave
                // cfShareReturn so login bounces back here. Any other outcome is
                // terminal, so clear it to avoid re-triggering on future app loads.
                if (err.response?.status === 401) return;
                sessionStorage.removeItem('cfShareReturn');
                if (code === 'NOT_ALLOWED') setPhase('not_allowed');
                else if (code === 'NOT_FOUND') setPhase('gone');
                else setPhase('error');
            });
    }, [token, navigate, app_url]);

    if (phase === 'view' && report) {
        return <SharedReportView title={report.title} blocks={report.blocks} expiresAt={report.expiresAt} />;
    }

    return (
        <div style={st.page}>
            <div style={st.card}>
                <span style={st.brand}>CognitiveUX.ai</span>
                {phase === 'accepting' && (
                    <>
                        <div style={st.spinner} />
                        <p style={st.text}>Opening the shared report…</p>
                    </>
                )}
                {phase === 'not_allowed' && (
                    <>
                        <h1 style={st.title}>This report wasn't shared with your account</h1>
                        <p style={st.text}>Ask the person who shared it to add your email, then open the link again.</p>
                        <button type="button" style={st.btn} onClick={() => navigate('/cognitive-flows/chat', { replace: true })}>Go to my chats</button>
                    </>
                )}
                {phase === 'no_store' && (
                    <>
                        <h1 style={st.title}>No access</h1>
                        <p style={st.text}>Shared reports open inside a CognitiveUX store. Finish setting yours up, then open this link again.</p>
                        <button type="button" style={st.btn} onClick={() => navigate('/setup-store', { replace: true })}>Complete store setup</button>
                    </>
                )}
                {phase === 'gone' && (
                    <>
                        <h1 style={st.title}>This link is no longer available</h1>
                        <p style={st.text}>It may have been revoked or replaced. Ask for a new link.</p>
                        <button type="button" style={st.btn} onClick={() => navigate('/cognitive-flows/chat', { replace: true })}>Go to my chats</button>
                    </>
                )}
                {phase === 'error' && (
                    <>
                        <h1 style={st.title}>Something went wrong</h1>
                        <p style={st.text}>Please try opening the link again.</p>
                    </>
                )}
            </div>
        </div>
    );
}
